/**
 * Helper per l'internazionalizzazione con fallback automatico
 * 
 * Funzione semplice che:
 * 1. Cerca la traduzione per la chiave data (TODO: da CSV o API)
 * 2. Se non trova nulla, ritorna il testo di fallback
 * 3. Non genera mai errori, sempre sicura da usare
 */

import { reactive } from 'vue';

// Cache per le traduzioni (reattiva)
const translationsMap = reactive<Record<string, string>>({});

// Controlla se il debug è abilitato tramite variabile ambiente
const isDebugEnabled = (): boolean => {
  return process.env.VUE_APP_I18N_DEBUG === 'true';
};

/**
 * Carica le traduzioni da fonte esterna (CSV, API, etc.)
 * Al momento ritorna un oggetto vuoto, le traduzioni sono nei fallback
 */
function loadTranslations(): Record<string, string> {
  // TODO: Qui potremmo caricare da:
  // - CSV file parsato
  // - API endpoint
  // - File JSON di traduzioni
  // - Database
  
  return translationsMap;
}

/**
 * Sostituisce i placeholder nel testo con i valori forniti
 * Es: "Totale {currentYear}" + { currentYear: 2025 } = "Totale 2025"
 * Supporta anche caratteri speciali come trattini: {CURRENT_YEAR-1}
 */
function replacePlaceholders(text: string, params: Record<string, any> = {}): string {
  return text.replace(/\{([\w-]+)\}/g, (match, key) => {
    return params[key] !== undefined ? params[key].toString() : match;
  });
}

/**
 * FUNZIONE PRINCIPALE
 * 
 * Ottiene il testo tradotto per una chiave, con fallback automatico
 * 
 * @param key - Chiave di traduzione (es: 'fr.deadlines.widget_title')
 * @param fallback - Testo di fallback se la chiave non esiste (SEMPRE usato per ora)
 * @param params - Parametri per sostituzioni dinamiche (es: {currentYear: 2025})
 * @returns Il testo tradotto o il fallback (con indicatore debug se VUE_APP_I18N_DEBUG=true)
 */
export function t(key: string, fallback: string, params: Record<string, any> = {}): string {
  try {
    const translations = loadTranslations();
    
    // Se esiste una traduzione, usala; altrimenti usa il fallback
    const translatedText = translations[key] || fallback;

    const processedText = replacePlaceholders(translatedText, params);

    if (isDebugEnabled()) {
      const debug_translated = `<span class='d-inline-block debug-translation-indicator' style='background:purple; color:white; font-size:10px; min-width:18px; height:18px;border-radius:50%;text-align:center; padding:1px 0 0 0; cursor:pointer;' title='Click to copy key' onclick="copyTranslationKey(event, '${key}')"><i class='ri-bug-fill'></i><span class='debug-key'>${key}</span></span>`;
      return processedText + debug_translated;
    }

    return processedText;
  } catch (e) {
    // In caso di qualsiasi errore, ritorna sempre il fallback processato
    return replacePlaceholders(fallback, params);
  }
}

/**
 * Imposta le traduzioni dall'esterno (CSV compilato dal cliente)
 * 
 * @param newTranslations - Oggetto con le traduzioni
 * @example
 * setTranslations({
 *   'fr.deadlines.widget_title': 'Dettagli Scadenze',
 *   'fr.tax_breakdown.legend_taxes': 'Imposte'
 * })
 */
export function setTranslations(newTranslations: Record<string, string>): void {
  Object.assign(translationsMap, newTranslations);
}

/**
 * Carica traduzioni da CSV parsato
 * 
 * @param csvData - Array di oggetti dal CSV
 * @example
 * loadFromCSV([
 *   { key: 'fr.deadlines.widget_title', testo_tradotto: 'Details Deadlines', nota: '...' }
 * ])
 */
export function loadFromCSV(csvData: Array<{ key: string; testo_tradotto: string; nota?: string }>): void {
  const translations: Record<string, string> = {};
  const currentYear = new Date().getFullYear();
  const params = { 'CURRENT_YEAR-1': currentYear - 1 };

  csvData.forEach(row => {
    if (row.key && row.testo_tradotto) {
      // Process placeholders in the translation text
      translations[row.key] = replacePlaceholders(row.testo_tradotto, params);
    }
  });

  setTranslations(translations);
}

/**
 * FUNZIONE ALTERNATIVA per compatibilità
 * Stessa logica ma con nome più breve
 */
export const getText = t;